import { useEffect, useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { api, list, money } from "@/lib/api";
import { Eyebrow } from "@/components/site/Reveal";
import { ListingsTab } from "@/components/admin/ListingsTab";
import { ClaimsTab } from "@/components/admin/ClaimsTab";
import { RequestsTab } from "@/components/admin/RequestsTab";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { ListingDialog } from "@/components/admin/ListingDialog";

const Stat = ({ label, value, testid }) => (
  <div className="border border-[#281245] bg-[#0b0514] p-6" data-testid={testid}>
    <p className="font-mono-j text-[11px] uppercase tracking-[0.25em] text-[#a78bce]">{label}</p>
    <p className="font-display mt-3 text-4xl text-[#f3eefc]">{value}</p>
  </div>
);

const TABS = [["listings", "Listings"], ["claims", "Claims"], ["requests", "Requests"], ["reviews", "Reviews"], ["analytics", "Analytics"]];

export default function Admin({ initialTab = "listings" }) {
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data || {})).catch(() => {});
    api.get("/products").then((r) => setProducts(list(r))).catch(() => {});
    api.get("/admin/claims").then((r) => setClaims(list(r))).catch(() => {});
    api.get("/admin/requests").then((r) => setRequests(list(r))).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(list(r))).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-[1400px] px-5 pb-32 pt-28 md:px-10 md:pt-32" data-testid="admin-page">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Eyebrow>Owner dashboard</Eyebrow>
          <h1 className="font-display mt-4 text-5xl text-[#f3eefc] md:text-6xl">The <span className="italic text-[#d8b4fe]">back room</span></h1>
        </div>
        <button onClick={() => setEditing({})} data-testid="admin-add-listing-button" className="inline-flex items-center gap-2 self-start rounded-full bg-[#7c3aed] px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#a855f7]">
          <Plus className="h-4 w-4" /> New listing
        </button>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Listings" value={stats.listings ?? 0} testid="stat-listings" />
        <Stat label="Available" value={stats.available ?? 0} testid="stat-available" />
        <Stat label="Open claims" value={stats.open_claims ?? 0} testid="stat-open-claims" />
        <Stat label="New requests" value={stats.new_requests ?? 0} testid="stat-new-requests" />
        <Stat label="Sold value" value={money(stats.sold_value)} testid="stat-sold-value" />
      </div>
      <div className="mt-12 flex gap-6 border-b border-[#281245]">
        {TABS.map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`} className={`shrink-0 border-b-2 pb-3 text-sm transition-colors ${tab === id ? "border-[#a855f7] text-white" : "border-transparent text-[#a78bce] hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "listings" && <ListingsTab products={products} onEdit={setEditing} reload={load} />}
        {tab === "claims" && <ClaimsTab claims={claims} reload={load} />}
        {tab === "requests" && <RequestsTab requests={requests} reload={load} />}
        {tab === "reviews" && <ReviewsTab reviews={reviews} reload={load} />}
        {tab === "analytics" && <AnalyticsTab />}
      </div>
      {editing && <ListingDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
